package com.trevorwiebe.timesheet.punch.domain.usecases

import com.trevorwiebe.timesheet.core.domain.model.Punch
import com.trevorwiebe.timesheet.core.domain.model.PunchType
import com.trevorwiebe.timesheet.punch.presentation.uiUtils.UiPunch
import kotlinx.datetime.LocalDateTime

class ProcessPunchesForUi {

    operator fun invoke(
        dateList: List<LocalDateTime>,
        punchList: List<Punch>,
        rateIdList: List<String>
    ): Map<LocalDateTime, Pair<List<UiPunch>, List<UiPunch>>> {

        val punchMap: MutableMap<LocalDateTime, Pair<List<UiPunch>, List<UiPunch>>> = mutableMapOf()
        val timePunchList = mutableListOf<Punch>()
        val ptoHolidayPunchList = mutableListOf<Punch>()

        punchList.forEach {
            if(rateIdList.contains(it.rateId)){
                ptoHolidayPunchList.add(it)
            }else{
                timePunchList.add(it)
            }
        }

        val sortedPtoPunchList = ptoHolidayPunchList.sortPunchList()
        val sortedTimePunchList = timePunchList.sortPunchList()

        val ptoPunchIn = sortedPtoPunchList.filter { it.type == PunchType.IN }
        val ptoPunchOut = sortedPtoPunchList.filter { it.type == PunchType.OUT }

        val timePunchIn = sortedTimePunchList.filter { it.type == PunchType.IN }
        val timePunchOut = sortedTimePunchList.filter { it.type == PunchType.OUT }

        dateList.forEach { today ->
            val dateTimeTodayStart = LocalDateTime(today.year, today.monthNumber, today.dayOfMonth, 0, 0, 0)
            val dateTimeTodayEnd = LocalDateTime(today.year, today.monthNumber, today.dayOfMonth, 23, 59, 59)

            val todayTimePunchesIn = timePunchIn.filter { it.dateTime in dateTimeTodayStart..dateTimeTodayEnd }
            val todayTimePunchesOut = timePunchOut.filter { it.dateTime in dateTimeTodayStart..dateTimeTodayEnd }
            val timeUiPunches = createUiPunchList(todayTimePunchesIn, todayTimePunchesOut)

            val ptoTodayPunchesIn = ptoPunchIn.filter { it.dateTime in dateTimeTodayStart..dateTimeTodayEnd }
            val ptoTodayPunchesOut = ptoPunchOut.filter { it.dateTime in dateTimeTodayStart..dateTimeTodayEnd }
            val ptoUiPunches = createUiPunchList(ptoTodayPunchesIn, ptoTodayPunchesOut)

            punchMap[today] = Pair(timeUiPunches, ptoUiPunches)
        }
        return punchMap
    }

    private fun formatTime(hour: Int, minute: Int): String {
        val h = if (hour % 12 == 0) 12 else hour % 12
        val m = minute.toString().padStart(2, '0')
        val amPm = if (hour < 12) "AM" else "PM"
        return "$h:$m $amPm"
    }

    private fun List<Punch>.sortPunchList(): List<Punch> {
        return this.sortedWith(
            compareBy<Punch> { it.rateId }.thenBy { it.dateTime }
        )
    }

    private fun createUiPunchList(punchIn: List<Punch>, punchOut: List<Punch>): List<UiPunch> {
        val timeUiPunches = mutableListOf<UiPunch>()
        punchIn.forEachIndexed { index, punchIn ->
            val punchOut = punchOut.getOrNull(index)
            val inReviewRequired = punchIn.reviewRequired == true
            val outReviewRequired = punchOut?.reviewRequired == true
            val punchInError: String?
            val punchOutError: String?
            when {
                inReviewRequired && outReviewRequired -> {
                    val msg = "Review required. Please update this punch."
                    punchInError = msg
                    punchOutError = msg
                }
                inReviewRequired -> {
                    punchInError = "Review required. Please update this punch."
                    punchOutError = null
                }
                outReviewRequired -> {
                    punchInError = null
                    punchOutError = "Review required. Please update this punch."
                }
                punchOut != null && punchIn.dateTime > punchOut.dateTime -> {
                    punchInError = null
                    punchOutError = "Clock out time must be after clock in time"
                }
                else -> {
                    punchInError = null
                    punchOutError = null
                }
            }
            timeUiPunches.add(UiPunch(punchIn, punchOut, punchInError, punchOutError))
        }
        return timeUiPunches
    }
}